import { v4 as uuid } from "uuid";

/** Keys that define the nested `band` map — everything else on a combined band is echoed from the API on save. */
const GROUP_ENVELOPE_STRIP = new Set(["band", "band_id", "unique_id"]);
/** Parsed explicitly on memo/text nodes; remaining entries are preserved on save. */
const MEMO_LEAF_STRIP = new Set(["text", "name", "band_id", "unique_id", "remove_section_break", "band"]);
/** Parsed explicitly on image nodes; remaining entries are preserved on save. */
const IMAGE_LEAF_STRIP = new Set(["data", "name", "band_id", "unique_id", "config", "remove_section_break", "show_captions", "band"]);

/**
 * @param {Record<string, any>} val
 * @param {Set<string>} stripKeys
 * @returns {Record<string, any>}
 */
function leafExtrasFrom(val, stripKeys) {
    if (!val || typeof val !== "object") return {};
    return Object.fromEntries(Object.entries(val).filter(([k]) => !stripKeys.has(k)));
}

/** @param {Record<string, any>|undefined} envelope */
function spreadApiEnvelope(envelope) {
    return envelope && typeof envelope === "object" ? { ...envelope } : {};
}

/** Persist BE `type` on tile nodes without touching FE `band_type`. */
function withBeType(node, payload) {
    if (!payload || !node) return payload || null;
    if (node.type != null && String(node.type) !== "") {
        return { ...payload, type: node.type };
    }
    return payload;
}

function nonEmptyString(value) {
    return value != null && String(value) !== "" ? String(value) : "";
}

/**
 * @typedef {Object} SelectionTreeNode
 * @property {string} selId
 * @property {'group'|'image'|'text'} band_type
 * @property {string} apiKey
 * @property {string} [name]
 * @property {string} [band_id]
 * @property {string} [unique_id] stable per-band instance id from API (preferred for selection + hierarchy identity)
 * @property {string} [text]
 * @property {Array<{ id?: string, band_id?: string }>} [data]
 * @property {object} [config]
 * @property {boolean} [isImageSlot]
 * @property {string} [imageRefId]
 * @property {boolean} [remove_section_break]
 * @property {boolean} [show_captions]
 * @property {Record<string, any>} [apiGroupEnvelope] combined-band fields from API except `band` (e.g. structured_heading)
 * @property {Record<string, any>} [apiLeafExtras] extra fields on memo/image bands from API
 * @property {SelectionTreeNode[]} [band]
 */

/**
 * @param {any} node
 * @param {string} key
 * @returns {boolean}
 */
function hierarchyNodeMatchesKey(node, key) {
    if (!node || key == null || key === "") return false;
    return node.unique_id === key || node.band_id === key;
}

/**
 * Stable key for checkbox selection / disabled lookups: API `unique_id`, else path `selId`.
 * @param {SelectionTreeNode|{ unique_id?: string, selId?: string }|null|undefined} node
 * @returns {string}
 */
export function bandSelectionKey(node) {
    if (!node) return "";
    const u = node.unique_id;
    if (u != null && String(u) !== "") return String(u);
    return node.selId || "";
}

/** @param {any[]|Record<string, any>|null|undefined} band */
function bandChildValues(band) {
    if (!band) return [];
    return Array.isArray(band) ? band : Object.values(band);
}

/**
 * Ordered children for UI: API `band` is `{ [apiKey]: node }`; legacy tiles may use an array.
 * @param {any[]|Record<string, any>|null|undefined} band
 * @returns {Array<any & { apiKey: string }>}
 */
export function bandChildrenAsArray(band) {
    if (!band) return [];
    if (Array.isArray(band)) {
        return band.map((n, i) => ({
            ...n,
            apiKey: n.apiKey
        }));
    }
    return Object.entries(band).map(([apiKey, n]) => ({ ...n, apiKey }));
}

/**
 * GET executive-summary payloads often omit `band_type`; nested `band` + leaf shape imply it.
 * @param {any} node
 * @returns {"group"|"image"|"text"|null}
 */
export function inferExecutiveSummaryHierarchyBandType(node) {
    if (!node || typeof node !== "object") return null;
    const t = node.band_type;
    if (t === "group" || t === "image" || t === "text") return t;
    if (node?.is_grouped_band === true) return "group";
    const nb = node.band;
    const hasNestedBand = nb && (Array.isArray(nb) ? nb.length > 0 : typeof nb === "object" && Object.keys(nb).length > 0);
    if (hasNestedBand) return "group";
    if (Array.isArray(node.data)) return "image";
    if (typeof node.text === "string") return "text";
    const key = String(node.apiKey ?? "");
    if (key.includes("list_image")) return "image";
    if (key.includes("memo_field")) return "text";
    return null;
}

/** @param {any} node */
export function withInferredHierarchyBandType(node) {
    if (!node || typeof node !== "object") return node;
    const inferred = inferExecutiveSummaryHierarchyBandType(node);
    if (!inferred || node.band_type === inferred) return node;
    return { ...node, band_type: inferred };
}

/**
 * Persist top-level (or nested) band list from config modal back to `{ apiKey: child }` shape.
 * @param {Array<{ apiKey: string } & Record<string, any>>} entries
 * @returns {Record<string, any>}
 */
export function hierarchyBandEntriesToRecord(entries) {
    if (!Array.isArray(entries) || !entries.length) return {};
    return Object.fromEntries(
        entries.map((e, i) => {
            const { apiKey, ...rest } = e;
            const key = apiKey && !String(apiKey).startsWith("__legacy_") ? apiKey : (rest.unique_id ?? rest.band_id ?? `__child_${i}`);
            return [key, rest];
        })
    );
}

/**
 * @typedef {Object} ChartHierarchyChild
 * @property {string} [id] legacy stable key; prefer `unique_id` for identity
 * @property {'group'|'image'|'text'} band_type
 * @property {string} [name]
 * @property {string} [unique_id] stable per-band id from API (preferred for find/patch/reorder)
 * @property {string} [band_id]
 * @property {string} [text]
 * @property {Record<string, any>} [apiGroupEnvelope] nested combined-band envelope from API (inner `band` groups)
 * @property {Record<string, any>} [apiLeafExtras] extra API fields on memo/image nodes
 * @property {Array<{ id?: string, band_id?: string }>} [data]
 * @property {object} [config]
 * @property {boolean} [show_captions]
 * @property {boolean} [remove_section_break]
 * @property {ChartHierarchyChild[]|Record<string, ChartHierarchyChild>} [band]
 */

export function makeSelId(pathKeys, suffix = "") {
    const base = (pathKeys || []).join(">");
    return suffix ? `${base}::${suffix}` : base;
}

function getBandChildApiKey(child, index) {
    const key =
        (child?.apiKey && String(child.apiKey)) ||
        (child?.key && String(child.key)) ||
        (child?.band_id != null && String(child.band_id) !== "" ? String(child.band_id) : "") ||
        (child?.unique_id != null && String(child.unique_id) !== "" ? String(child.unique_id) : "") ||
        `__band_${index}`;
    return key;
}

/** Normalize API `band` payloads that may be a map or ordered array of mixed nested/single bands. */
export function normalizeExecutiveSummaryBandRecord(band) {
    if (!band) return {};
    if (!Array.isArray(band)) {
        return typeof band === "object" ? band : {};
    }
    return Object.fromEntries(
        band.map((child, index) => [getBandChildApiKey(child, index), child]).filter(([, child]) => child && typeof child === "object")
    );
}
/**
 * @param {Record<string, any>} executiveSummary
 * @returns {SelectionTreeNode[]}
 */
export function buildExecutiveSummarySelectionForest(executiveSummary) {
    if (!executiveSummary || typeof executiveSummary !== "object") return [];
    return Object.entries(executiveSummary).map(([apiKey, raw]) => parseValueToNode(apiKey, raw, [apiKey]));
}

function collectExistingTileIdentitySets(existingTilesByKey) {
    const ids = {
        selectionKeys: new Set(),
        apiKeys: new Set(),
        bandIds: new Set(),
        uniqueIds: new Set()
    };

    const addNode = (node, apiKey) => {
        if (!node || typeof node !== "object") return;
        const effectiveApiKey = nonEmptyString(node.apiKey) || nonEmptyString(apiKey);
        const uniqueId = nonEmptyString(node.unique_id);
        const bandId = nonEmptyString(node.band_id);
        const selectionKey = nonEmptyString(bandSelectionKey(node));

        if (effectiveApiKey && !effectiveApiKey.startsWith("__legacy_")) ids.apiKeys.add(effectiveApiKey);
        if (uniqueId) ids.uniqueIds.add(uniqueId);
        if (bandId) ids.bandIds.add(bandId);
        if (selectionKey) ids.selectionKeys.add(selectionKey);

        const children = node.band;
        if (Array.isArray(children)) {
            children.forEach(child => addNode(child, child?.apiKey));
            return;
        }
        if (children && typeof children === "object") {
            Object.entries(children).forEach(([childApiKey, child]) => addNode(child, childApiKey));
        }
    };

    Object.entries(existingTilesByKey || {}).forEach(([chartKey, tile]) => {
        if (chartKey) ids.apiKeys.add(String(chartKey));
        addNode(tile, chartKey);
    });

    return ids;
}

function nodeMatchesExistingTileIdentity(node, existingIds) {
    const selectionKey = nonEmptyString(bandSelectionKey(node));
    const apiKey = nonEmptyString(node?.apiKey);
    const bandId = nonEmptyString(node?.band_id);
    const uniqueId = nonEmptyString(node?.unique_id);

    return (
        (selectionKey && existingIds.selectionKeys.has(selectionKey)) ||
        (apiKey && existingIds.apiKeys.has(apiKey)) ||
        (bandId && existingIds.bandIds.has(bandId)) ||
        (uniqueId && existingIds.uniqueIds.has(uniqueId))
    );
}

export function collectExecutiveSummaryDisabledSelectionIds(executiveSummaryTree, existingTilesByKey) {
    const existingIds = collectExistingTileIdentitySets(existingTilesByKey);
    const disabled = new Set();

    const walk = nodes => {
        (nodes || []).forEach(node => {
            const key = bandSelectionKey(node);
            if (key && nodeMatchesExistingTileIdentity(node, existingIds)) {
                disabled.add(key);
            }
            if (node.band?.length) walk(node.band);
        });
    };
    walk(executiveSummaryTree);

    return disabled;
}

/**
 * @param {string} apiKey
 * @param {any} val
 * @param {string[]} pathKeys
 * @returns {SelectionTreeNode}
 */
function parseValueToNode(apiKey, val, pathKeys, parentName) {
    if (!val || typeof val !== "object") {
        return {
            selId: makeSelId(pathKeys),
            band_type: "text",
            apiKey,
            name: parentName,
            text: ""
        };
    }

    const nodeWithKey = { ...val, apiKey };
    const inferredType = inferExecutiveSummaryHierarchyBandType(nodeWithKey);

    if (inferredType === "group") {
        const bandRecord = normalizeExecutiveSummaryBandRecord(val.band);
        const apiGroupEnvelope = leafExtrasFrom(val, GROUP_ENVELOPE_STRIP);
        const band = Object.entries(bandRecord).map(([k, v]) => parseValueToNode(k, v, [...pathKeys, k]));
        return {
            ...val,
            selId: makeSelId(pathKeys),
            band_type: "group",
            apiKey,
            name: val.name,
            band_id: val.band_id,
            unique_id: val.unique_id,
            is_grouped_band: val.is_grouped_band === true,
            remove_section_break: typeof val.remove_section_break === "boolean" ? val.remove_section_break : undefined,
            apiGroupEnvelope,
            band
        };
    }

    if (inferredType === "text") {
        const apiLeafExtras = leafExtrasFrom(val, MEMO_LEAF_STRIP);
        return {
            ...val,
            selId: makeSelId(pathKeys),
            band_type: "text",
            apiKey,
            name: val.name,
            band_id: val.band_id,
            unique_id: val.unique_id,
            text: val.text || "",
            remove_section_break: typeof val.remove_section_break === "boolean" ? val.remove_section_break : undefined,
            apiLeafExtras: Object.keys(apiLeafExtras).length ? apiLeafExtras : undefined
        };
    }

    const data = Array.isArray(val.data) ? val.data : [];
    const apiLeafExtras = leafExtrasFrom(val, IMAGE_LEAF_STRIP);

    return {
        ...val,
        selId: makeSelId(pathKeys),
        band_type: "image",
        apiKey,
        name: val.name,
        band_id: val.band_id,
        unique_id: val.unique_id,
        data: [...data],
        config: val.config && typeof val.config === "object" ? { ...val.config } : { type: 1 },
        remove_section_break: typeof val.remove_section_break === "boolean" ? val.remove_section_break : undefined,
        show_captions: typeof val.show_captions === "boolean" ? val.show_captions : undefined,
        apiLeafExtras: Object.keys(apiLeafExtras).length ? apiLeafExtras : undefined
    };
}

/**
 * @param {SelectionTreeNode} node
 * @returns {string[]}
 */
export function collectDescendantSelIds(node) {
    const out = [bandSelectionKey(node)];
    if (node.band?.length) {
        node.band.forEach(c => out.push(...collectDescendantSelIds(c)));
    }
    return out;
}

/**
 * All descendant selection ids under `node`, excluding the node's own `selId`
 * (used for "select all inner" controls on parent rows).
 * @param {SelectionTreeNode} node
 * @returns {string[]}
 */
export function collectInnerDescendantSelIds(node) {
    if (!node.band?.length) return [];
    const out = [];
    node.band.forEach(c => out.push(...collectDescendantSelIds(c)));
    return out;
}

function areAllInnerDescendantsSelected(node, selected) {
    const set = selected instanceof Set ? selected : new Set(selected);
    const innerIds = collectInnerDescendantSelIds(node);
    return innerIds?.length > 0 && innerIds?.every(descSelId => set?.has(descSelId));
}

/**
 * Whether any descendant (not including `node` itself) appears in the selection set.
 * @param {SelectionTreeNode} node
 * @param {Set<string>} set
 */
export function hasDescendantInSelection(node, set) {
    for (const c of node.band || []) {
        if (set.has(bandSelectionKey(c))) return true;
        if (hasDescendantInSelection(c, set)) return true;
    }
    return false;
}

/**
 * Checkbox row state: parent/group rows use an independent checked state for the node itself;
 * indeterminate reflects inner selections without implying "select all".
 * @param {SelectionTreeNode} node
 * @param {Set<string>|string[]} selected
 * @returns {{ checked: boolean, indeterminate: boolean }}
 */
export function getRowCheckboxState(node, selected) {
    const set = selected instanceof Set ? selected : new Set(selected);
    const key = bandSelectionKey(node);
    const checked = set.has(key);
    const hasChildren = !!(node.band && node.band.length);
    if (!hasChildren) {
        return { checked, indeterminate: false };
    }
    return {
        checked,
        indeterminate: !checked && hasDescendantInSelection(node, set)
    };
}

/**
 * Materialize a full chart subtree from a selection tree node (no selection filter).
 * @param {SelectionTreeNode} node
 * @returns {ChartHierarchyChild|null}
 */
export function selectionNodeToChartNodeFull(node) {
    if (!node || node.isImageSlot) {
        return null;
    }
    const { apiGroupEnvelope, apiLeafExtras, apiKey, selId, ...restNodeKeys } = node;

    if (node.band_type === "text") {
        return {
            ...restNodeKeys,
            band_type: "text",
            type: node?.type,
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            text: node.text || "",
            remove_section_break: node.remove_section_break !== undefined ? node.remove_section_break : false,
            is_building_exec_sum: true,
            ...spreadApiEnvelope(node.apiLeafExtras)
        };
    }
    if (node.band_type === "image") {
        return {
            ...restNodeKeys,
            band_type: "image",
            type: node?.type,
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            data: [...(node.data || [])],
            config: { ...(node.config || { type: 1 }) },
            show_captions: node.show_captions !== undefined ? node.show_captions : true,
            remove_section_break: node.remove_section_break !== undefined ? node.remove_section_break : false,
            is_building_exec_sum: true,
            ...spreadApiEnvelope(node.apiLeafExtras)
        };
    }
    if (node.band_type === "group") {
        const band = {};
        for (const c of node.band || []) {
            const child = selectionNodeToChartNodeFull(c);
            if (child) {
                const { apiKey, selId, ...cleanChild } = child;
                band[c.apiKey] = cleanChild;
            }
        }
        return {
            ...restNodeKeys,
            type: node?.type,
            band_type: "group",
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            ...spreadApiEnvelope(node.apiGroupEnvelope),
            is_building_exec_sum: true,
            is_grouped_band: node.is_grouped_band === true,
            be_select_all: true,
            band
        };
    }
    return null;
}

/**
 * @param {SelectionTreeNode} node
 * @param {Set<string>} selected
 * @returns {ChartHierarchyChild|null}
 */
export function selectionNodeToChartNode(node, selected) {
    if (node.isImageSlot) {
        return null;
    }

    if (node.band_type === "text") {
        const { apiGroupEnvelope, apiLeafExtras, apiKey, selId, ...restNodeKeys } = node;
        if (!selected.has(bandSelectionKey(node))) return null;

        return {
            ...restNodeKeys,
            band_type: "text",
            type: node?.type,
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            text: node.text || "",
            is_building_exec_sum: true,
            ...spreadApiEnvelope(node.apiLeafExtras)
        };
    }

    if (node.band_type === "image") {
        const { apiGroupEnvelope, apiLeafExtras, apiKey, selId, ...restNodeKeys } = node;
        if (!selected.has(bandSelectionKey(node))) return null;
        return {
            ...restNodeKeys,
            band_type: "image",
            type: node?.type,
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            data: [...(node.data || [])],
            config: { ...(node.config || { type: 1 }) },
            show_captions: node.show_captions !== undefined ? node.show_captions : true,
            remove_section_break: node.remove_section_break !== undefined ? node.remove_section_break : false,
            is_building_exec_sum: true,
            ...spreadApiEnvelope(node.apiLeafExtras)
        };
    }

    if (node.band_type === "group") {
        const { apiGroupEnvelope, apiLeafExtras, apiKey, selId, ...restNodeKeys } = node;
        const sel = selected instanceof Set ? selected : new Set(selected);
        const allDescIds = collectDescendantSelIds(node);
        const isFullySelected = allDescIds.length > 0 && allDescIds.every(descSelId => sel.has(descSelId));
        if (sel.has(bandSelectionKey(node)) && isFullySelected) {
            const full = selectionNodeToChartNodeFull(node);
            return full && full.band_type === "group" ? full : null;
        }
        /** @type {Record<string, any>} */
        const band = {};
        for (const c of node.band || []) {
            const child = selectionNodeToChartNode(c, selected);
            if (child) band[c.apiKey] = child;
        }
        if (!Object.keys(band).length && sel.has(bandSelectionKey(node))) {
            return {
                ...restNodeKeys,
                band_type: "group",
                type: node?.type,
                name: node.name,
                band_id: node.band_id,
                unique_id: node.unique_id,
                ...spreadApiEnvelope(node.apiGroupEnvelope),
                is_building_exec_sum: true,
                is_grouped_band: node.is_grouped_band === true,
                be_select_all: false,
                band: {}
            };
        }
        if (!Object.keys(band).length) return null;
        return {
            ...restNodeKeys,
            band_type: "group",
            type: node?.type,
            name: node.name,
            band_id: node.band_id,
            unique_id: node.unique_id,
            ...spreadApiEnvelope(node.apiGroupEnvelope),
            is_building_exec_sum: true,
            is_grouped_band: node.is_grouped_band === true,
            be_select_all: areAllInnerDescendantsSelected(node, sel),
            band
        };
    }

    return null;
}

export const MULTI_BUILDING_GROUPED_BAND_TILE_KEY = "multi_exec_sum_comb_band";
export const MULTI_BUILDING_GROUPED_BAND_TILE_NAME = "BBI Executive Summary - Grouped Band";

export const EXEC_SUM_TILE_SCOPE = {
    SINGLE: "single",
    MULTI_GROUPED: "multi_grouped"
};

export function executiveSummaryTileScopeFromMode(mode) {
    if (mode === "grouped") return EXEC_SUM_TILE_SCOPE.MULTI_GROUPED;
    return EXEC_SUM_TILE_SCOPE.SINGLE;
}

/** Classify persisted executive-summary tiles so each multi-building mode merges independently. */
export function getExecutiveSummaryTileScope(tile, key = "") {
    if (!tile?.is_building_exec_sum) return null;
    if (key === MULTI_BUILDING_GROUPED_BAND_TILE_KEY) {
        return EXEC_SUM_TILE_SCOPE.MULTI_GROUPED;
    }
    return EXEC_SUM_TILE_SCOPE.SINGLE;
}

/**
 * Multiple-building "Grouped Bands" tab — one parent tile containing all selected top-level bands.
 * @param {SelectionTreeNode[]} executiveSummaryTree
 * @param {Set<string>} selected
 * @returns {Record<string, any>}
 */
export function buildMultiBuildingGroupedExecutiveSummaryTile(executiveSummaryTree, selected) {
    const sel = selected instanceof Set ? selected : new Set(selected);
    const band = {};

    (executiveSummaryTree || []).forEach(root => {
        const child = selectionNodeToChartNode(root, sel);
        if (!child) return;
        band[root.apiKey] = child;
    });

    if (!Object.keys(band).length) return {};

    const allDescendantIds = (executiveSummaryTree || []).flatMap(root => collectDescendantSelIds(root));
    const be_select_all = allDescendantIds.length > 0 && allDescendantIds.every(id => sel.has(id));

    return {
        [MULTI_BUILDING_GROUPED_BAND_TILE_KEY]: {
            name: MULTI_BUILDING_GROUPED_BAND_TILE_NAME,
            exec_sum_parent_wrapper: true,
            is_building_exec_sum: true,
            is_grouped_band: true,
            group_by: "building",
            sort_by: "building_name",
            sort_direction: "asc",
            structured_heading: {
                heading_style: "Heading 1"
            },
            group_by_heading: "BBI Executive Summary",
            with_export: false,
            heading_prefix: "building_name",
            be_select_all,
            band
        }
    };
}

/**
 * @param {SelectionTreeNode} root
 * @param {Set<string>} selected
 * @returns {Record<string, any>|null} Saved tile: combined groups use `exec_sum_*` + nested `band`; standalone image/memo match GET payload (flat, `band_type`).
 */
export function buildHierarchyTilePayload(root, selected) {
    const any = collectDescendantSelIds(root).some(descSelId => selected.has(descSelId));
    if (!any) return null;

    if (root.band_type === "group") {
        const groupEcho = spreadApiEnvelope(root.apiGroupEnvelope);
        const sel = selected instanceof Set ? selected : new Set(selected);
        const allDescIds = collectDescendantSelIds(root);
        const isFullySelected = allDescIds.length > 0 && allDescIds.every(descSelId => sel.has(descSelId));
        if (sel.has(bandSelectionKey(root)) && isFullySelected) {
            const full = selectionNodeToChartNodeFull(root);
            if (!full || full.band_type !== "group") return null;
            return {
                ...groupEcho,
                name: root.name,
                type: root?.type,
                band_id: root.band_id,
                unique_id: root.unique_id,
                exec_sum_parent_wrapper: true,
                remove_section_break: root.remove_section_break !== undefined ? root.remove_section_break : false,
                is_building_exec_sum: true,
                is_grouped_band: root.is_grouped_band === true,
                be_select_all: true,
                band: full.band
            };
        }
        let band = {};
        for (const c of root.band || []) {
            const child = selectionNodeToChartNode(c, selected);
            if (child) band[c.apiKey] = child;
        }
        if (!Object.keys(band).length && sel.has(bandSelectionKey(root))) {
            return {
                ...groupEcho,
                name: root.name,
                type: root?.type,
                band_id: root.band_id,
                unique_id: root.unique_id,
                exec_sum_parent_wrapper: true,
                remove_section_break: root.remove_section_break !== undefined ? root.remove_section_break : false,
                is_building_exec_sum: true,
                is_grouped_band: root.is_grouped_band === true,
                be_select_all: false,
                band: {}
            };
        }
        if (!Object.keys(band).length) return null;
        return {
            ...groupEcho,
            name: root.name,
            type: root?.type,
            band_id: root.band_id,
            unique_id: root.unique_id,
            exec_sum_parent_wrapper: true,
            remove_section_break: root.remove_section_break !== undefined ? root.remove_section_break : false,
            is_building_exec_sum: true,
            is_grouped_band: root.is_grouped_band === true,
            be_select_all: areAllInnerDescendantsSelected(root, sel),
            band
        };
    }

    const one = selectionNodeToChartNode(root, selected);
    if (!one) return null;
    if (one.band_type === "image" || one.band_type === "text") {
        return { ...one };
    }
    return {
        name: one.name || root.name,
        type: one?.type || root?.type,
        band_id: one.band_id ?? root.band_id,
        unique_id: one.unique_id ?? root.unique_id,
        exec_sum_parent_wrapper: false,
        remove_section_break: root.remove_section_break !== undefined ? root.remove_section_break : false,
        is_building_exec_sum: false,
        band: { [root.apiKey]: one }
    };
}

/**
 * @param {SelectionTreeNode[]} executiveSummaryTree
 * @param {Set<string>} selected
 * @param {(apiKey: string) => string} [keyFactory]
 * @returns {Record<string, any>}
 */
export function buildExecutiveSummaryHierarchyTilesMap(executiveSummaryTree, selected, keyFactory) {
    const tiles = {};
    (executiveSummaryTree || []).forEach(root => {
        const payload = buildHierarchyTilePayload(root, selected);
        if (!payload) return;
        const chartKey = typeof keyFactory === "function" ? keyFactory(root.apiKey) : `exec_sum_hierarchy_${uuid().replace(/-/g, "")}`;
        tiles[chartKey] = payload;
    });
    return tiles;
}

function hierarchyNodeIdentityMatches(a, b) {
    if (!a || !b) return false;
    const aBandId = nonEmptyString(a.band_id);
    const bBandId = nonEmptyString(b.band_id);
    if (aBandId && bBandId && aBandId === bBandId) return true;
    const aUniqueId = nonEmptyString(a.unique_id);
    const bUniqueId = nonEmptyString(b.unique_id);
    if (aUniqueId && bUniqueId && aUniqueId === bUniqueId) return true;
}

function hierarchyOrderIdentityKeys(node) {
    return [nonEmptyString(node?.band_id)].filter(Boolean);
}

/**
 * Index child bands by their BE/source order for later merges into existing parent tiles.
 * @param {SelectionTreeNode[]} executiveSummaryTree
 * @returns {Record<string, Record<string, number>>}
 */
export function buildExecutiveSummaryHierarchyOrderMap(executiveSummaryTree) {
    const orderMap = {};
    const walk = nodes => {
        (nodes || []).forEach(node => {
            if (node.band?.length) {
                const childOrder = {};
                node.band.forEach((child, index) => {
                    hierarchyOrderIdentityKeys(child).forEach(key => {
                        childOrder[key] = index;
                    });
                });
                hierarchyOrderIdentityKeys(node).forEach(parentKey => {
                    orderMap[parentKey] = childOrder;
                });
                walk(node.band);
            }
        });
    };
    walk(executiveSummaryTree);
    return orderMap;
}

function bandRecordFromAny(band) {
    if (!band) return {};
    if (!Array.isArray(band)) return { ...band };
    return Object.fromEntries(
        band.map((child, index) => {
            const key = child?.apiKey ? child.apiKey : (child?.band_id ?? child?.unique_id ?? `__child_${index}`);
            const { apiKey, ...cleanChild } = child || {};
            return [key, cleanChild];
        })
    );
}

function findMatchingBandEntryKey(existingBandRecord, incomingKey, incomingChild) {
    if (incomingKey && existingBandRecord?.[incomingKey]) return incomingKey;
    const match = Object.entries(existingBandRecord).find(([, existingChild]) => hierarchyNodeIdentityMatches(existingChild, incomingChild));
    return match?.[0] || "";
}

function orderIndexForBandEntry(childOrder, entryKey, child, fallbackIndex) {
    const indexes = hierarchyOrderIdentityKeys(child, entryKey)
        .map(key => childOrder?.[key])
        .filter(index => typeof index === "number");
    return indexes.length ? Math.min(...indexes) : Number.MAX_SAFE_INTEGER + fallbackIndex;
}

function sortBandRecordByHierarchyOrder(bandRecord, parentNode, orderMap = {}) {
    const childOrder = hierarchyOrderIdentityKeys(parentNode)
        .map(key => orderMap?.[key])
        .find(Boolean);
    if (!childOrder) return bandRecord;
    return Object.fromEntries(
        Object.entries(bandRecord)
            .map(([key, child], index) => ({ key, child, order: orderIndexForBandEntry(childOrder, key, child, index) }))
            .sort((a, b) => a.order - b.order)
            .map(({ key, child }) => [key, child])
    );
}

function mergeHierarchyNode(existingNode, incomingNode, orderMap = {}) {
    if (!existingNode || !incomingNode) return existingNode || incomingNode || null;
    if (!incomingNode.band) return existingNode;
    return {
        ...existingNode,
        ...(Object.prototype.hasOwnProperty.call(incomingNode, "be_select_all") ? { be_select_all: incomingNode.be_select_all } : {}),
        band: mergeHierarchyBandRecords(existingNode.band, incomingNode.band, incomingNode || existingNode, orderMap)
    };
}

function mergeHierarchyBandRecords(existingBand, incomingBand, parentNode, orderMap = {}) {
    const merged = bandRecordFromAny(existingBand);
    const incoming = bandRecordFromAny(incomingBand);
    Object.entries(incoming).forEach(([incomingKey, incomingChild]) => {
        const matchKey = findMatchingBandEntryKey(merged, incomingKey, incomingChild);
        if (matchKey) {
            merged[matchKey] = mergeHierarchyNode(merged[matchKey], incomingChild, orderMap);
            return;
        }
        merged[incomingKey] = incomingChild;
    });
    return sortBandRecordByHierarchyOrder(merged, parentNode, orderMap);
}

function findExistingHierarchyTileKey(existingTypes, incomingKey, incomingTile) {
    if (incomingKey && existingTypes?.[incomingKey]) return incomingKey;
    const incomingBandId = nonEmptyString(incomingTile?.band_id);
    if (!incomingBandId) return "";
    const match = Object.entries(existingTypes || {}).find(([, tile]) => nonEmptyString(tile?.band_id) === incomingBandId);
    return match?.[0] || "";
}

function pruneHierarchyNodeToIncomingSelection(existingNode, incomingNode, orderMap = {}) {
    if (!existingNode || !incomingNode) return null;
    if (!incomingNode.band) return existingNode;

    const existingBand = bandRecordFromAny(existingNode.band);
    const incomingBand = bandRecordFromAny(incomingNode.band);
    const band = {};

    Object.entries(incomingBand).forEach(([incomingKey, incomingChild]) => {
        const matchKey = findMatchingBandEntryKey(existingBand, incomingKey, incomingChild);
        const existingChild = matchKey ? existingBand[matchKey] : null;
        if (!existingChild) return;
        const prunedChild = pruneHierarchyNodeToIncomingSelection(existingChild, incomingChild, orderMap);
        if (prunedChild) band[matchKey || incomingKey] = prunedChild;
    });

    return {
        ...existingNode,
        band: sortBandRecordByHierarchyOrder(band, incomingNode || existingNode, orderMap)
    };
}

/**
 * Reconcile Executive Summary hierarchy tiles to the incoming selection shape while preserving existing edited node data.
 * @param {Record<string, any>} existingTypes
 * @param {Record<string, any>} incomingTiles
 * @param {Record<string, Record<string, number>>} [orderMap]
 * @returns {{ types: Record<string, any>, addedKeys: string[] }}
 */
export function mergeExecutiveSummaryHierarchyTilesIntoTypes(existingTypes = {}, incomingTiles = {}, orderMap = {}, options = {}) {
    const tileScopes = options?.tileScopes?.length ? options.tileScopes : options?.tileScope ? [options.tileScope] : null;
    const types = Object.fromEntries(
        Object.entries(existingTypes || {}).filter(([key, tile]) => {
            if (!tile?.is_building_exec_sum) return true;
            if (!tileScopes?.length) return false;
            const scope = getExecutiveSummaryTileScope(tile, key);
            return !tileScopes.includes(scope);
        })
    );
    const addedKeys = [];

    Object.entries(incomingTiles || {}).forEach(([incomingKey, incomingTile]) => {
        const existingKey = findExistingHierarchyTileKey(existingTypes, incomingKey, incomingTile);
        if (existingKey) {
            const prunedExistingTile = pruneHierarchyNodeToIncomingSelection(existingTypes[existingKey], incomingTile, orderMap);
            types[existingKey] = mergeHierarchyNode(prunedExistingTile, incomingTile, orderMap);
            addedKeys.push(existingKey);
            return;
        }
        types[incomingKey] = incomingTile;
        addedKeys.push(incomingKey);
    });

    return { types, addedKeys };
}

/**
 * @param {any} node
 * @param {string} id
 * @returns {any|null}
 */
export function findHierarchyNodeById(node, id) {
    if (!node) return null;
    if (hierarchyNodeMatchesKey(node, id)) return node;
    for (const c of bandChildValues(node.band)) {
        const hit = findHierarchyNodeById(c, id);
        if (hit) return hit;
    }
    return null;
}

/**
 * @param {{ band?: any[]|Record<string, any> }} tile
 * @param {string} id
 */
export function findHierarchyChildInTile(tile, id) {
    for (const c of bandChildValues(tile?.band)) {
        const hit = findHierarchyNodeById(c, id);
        if (hit) return hit;
    }
    return null;
}

/**
 * @param {ChartHierarchyChild[]} list
 * @param {string} activeId
 * @param {string} overId
 * @returns {ChartHierarchyChild[]|null}
 */
export function reorderSiblingsById(list, activeId, overId) {
    if (!Array.isArray(list) || !activeId || !overId || activeId === overId) return null;
    const oldIndex = list.findIndex(n => hierarchyNodeMatchesKey(n, activeId));
    const newIndex = list.findIndex(n => hierarchyNodeMatchesKey(n, overId));
    if (oldIndex < 0 || newIndex < 0) return null;
    const next = [...list];
    const [removed] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, removed);
    return next;
}

/**
 * @param {ChartHierarchyChild[]|Record<string, ChartHierarchyChild>} band
 * @param {string} removeId
 * @returns {any[]|Record<string, any>}
 */
export function removeChildByIdFromList(band, removeId) {
    if (!band) return band;
    if (Array.isArray(band)) {
        let changed = false;
        const next = band
            .filter(c => {
                const shouldRemove = hierarchyNodeMatchesKey(c, removeId);
                if (shouldRemove) changed = true;
                return !shouldRemove;
            })
            .map(c => {
                const nb = c.band;
                const hasNested = nb && (Array.isArray(nb) ? nb.length : Object.keys(nb).length);
                if (!hasNested) return c;
                const nextBand = removeChildByIdFromList(nb, removeId);
                if (nextBand === nb) return c;
                changed = true;
                return { ...c, be_select_all: false, band: nextBand };
            });
        return changed ? next : band;
    }
    const out = { ...band };
    let changed = false;
    for (const key of Object.keys(out)) {
        const c = out[key];
        if (hierarchyNodeMatchesKey(c, removeId)) {
            delete out[key];
            changed = true;
            continue;
        }
        const nb = c.band;
        const hasNested = nb && (Array.isArray(nb) ? nb.length : Object.keys(nb).length);
        if (hasNested) {
            const nextBand = removeChildByIdFromList(nb, removeId);
            if (nextBand !== nb) {
                changed = true;
                out[key] = { ...c, be_select_all: false, band: nextBand };
            }
        }
    }
    return changed ? out : band;
}

/**
 * Deep-merge patch for one node in hierarchy tile band.
 * @param {any} tile
 * @param {string} targetId
 * @param {(n: any) => any} patchFn
 */
export function patchHierarchyNodeById(tile, targetId, patchFn) {
    const patchNode = node => {
        if (hierarchyNodeMatchesKey(node, targetId)) return patchFn({ ...node });
        const nb = node.band;
        const hasNested = nb && (Array.isArray(nb) ? nb.length : Object.keys(nb).length);
        if (!hasNested) return { ...node };
        return { ...node, band: patchBand(nb) };
    };
    const patchBand = b => {
        if (!b) return b;
        if (Array.isArray(b)) return b.map(n => patchNode(n));
        return Object.fromEntries(Object.entries(b).map(([k, n]) => [k, patchNode(n)]));
    };
    return { ...tile, band: patchBand(tile.band ?? {}) };
}

export const getEmptyImageBandConfig = () => ({
    data: [],
    config: { type: 1 },
    remove_section_break: false,
    show_captions: true
});

export const clearExecutiveSummaryImageBands = tile => {
    if (!tile?.is_building_exec_sum) return tile;

    const visited = new WeakSet();

    const clearNode = node => {
        if (!node || typeof node !== "object") return node;

        if (visited.has(node)) return node;
        visited.add(node);

        const children = node.band;

        const hasChildren = Array.isArray(children) ? children.length > 0 : !!(children && Object.keys(children).length);

        if (hasChildren) {
            const band = Array.isArray(children)
                ? children.map(clearNode)
                : Object.fromEntries(Object.entries(children).map(([key, child]) => [key, clearNode(child)]));

            return { ...node, band };
        }

        if (node.band_type === "image" || Array.isArray(node.data)) {
            return {
                ...node,
                ...getEmptyImageBandConfig()
            };
        }

        return node;
    };

    return clearNode(tile);
};
